
import { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useFullscreen } from './useFullscreen';

interface TestSecurityOptions {
  onViolation?: (violationType: string, count: number) => void;
  maxViolations?: number;
  enableFullscreen?: boolean;
  blockKeyboardShortcuts?: boolean;
  disableF12Key?: boolean; // Separate control for F12
  allowedKeys?: string[]; // New option to specify allowed keys
  preventTabSwitching?: boolean;
  disableRightClick?: boolean;
  preventResize?: boolean;
}

export function useTestSecurity({
  onViolation,
  maxViolations = 3,
  enableFullscreen = true,
  blockKeyboardShortcuts = true,
  disableF12Key = true, // Default is true
  allowedKeys = [], // Default is empty array
  preventTabSwitching = true,
  disableRightClick = true,
  preventResize = true,
}: TestSecurityOptions = {}) {
  const [violations, setViolations] = useState<{
    [key: string]: number
  }>({
    total: 0,
    tabSwitch: 0,
    keyboardShortcut: 0,
    rightClick: 0,
    windowResize: 0,
    windowBlur: 0,
    f12Key: 0
  });
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const { isFullscreen, enterFullscreen, exitFullscreen } = useFullscreen();
  const [securityWarningShown, setSecurityWarningShown] = useState<boolean>(false);
  
  // Refs to track enabled state - these won't trigger re-renders when changed
  const enabledRef = useRef({
    disableF12Key,
    blockKeyboardShortcuts,
    allowedKeys
  });
  
  // Update refs when props change
  useEffect(() => {
    enabledRef.current = {
      disableF12Key,
      blockKeyboardShortcuts,
      allowedKeys
    };
    console.log('Security settings updated:', { disableF12Key, blockKeyboardShortcuts, allowedKeys });
  }, [disableF12Key, blockKeyboardShortcuts, allowedKeys]);

  // Improved violation recording
  const recordViolation = useCallback((type: string) => {
    if (isLocked) return;
    
    setViolations(prev => {
      const newViolations = {
        ...prev,
        [type]: (prev[type] || 0) + 1,
        total: prev.total + 1
      };
      
      // Custom logging for violation types
      const violationMessages = {
        tabSwitch: "Switching tabs during the test is not allowed",
        keyboardShortcut: "Prohibited keyboard shortcut detected",
        f12Key: "Developer tools access is restricted",
        rightClick: "Right-clicking is disabled during the test",
        windowResize: "Window resizing is not permitted",
        windowBlur: "Minimizing or switching windows is not allowed"
      };
      
      // Show specific toast for each violation type
      const message = violationMessages[type] || "Security violation detected";
      toast.warning(message, {
        description: `Violation ${newViolations[type]} of ${maxViolations}. Further attempts may lock your test.`,
        duration: 4000,
        position: "top-right",
        icon: "🚨"
      });
      
      // Lock test if max violations reached
      if (newViolations.total >= maxViolations) {
        setIsLocked(true);
        toast.error("Test Locked: Multiple security violations detected. Please contact your administrator.", {
          description: "Your test has been terminated due to repeated security breaches.",
          duration: 8000,
          position: "top-center"
        });
      }
      
      // Call optional violation callback
      if (onViolation) {
        onViolation(type, newViolations[type]);
      }
      
      return newViolations;
    });
  }, [isLocked, maxViolations, onViolation]);

  // Handle keyboard events with improved F12 detection
  useEffect(() => {
    // Direct F12 key handler - this is the most reliable way to block F12
    const handleF12 = (e: KeyboardEvent) => {
      // Skip if F12 blocking is disabled or the key is in allowed keys
      if (!enabledRef.current.disableF12Key || enabledRef.current.allowedKeys.includes('F12')) {
        return true;
      }
      
      // Only handle F12 key
      if (e.key === 'F12' || e.keyCode === 123) {
        e.preventDefault();
        e.stopPropagation();
        console.log('F12 key blocked');
        recordViolation('f12Key');
        return false;
      }
    };
    
    // General keyboard shortcuts handler
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if the key is in the allowed keys list
      if (enabledRef.current.allowedKeys.includes(e.key)) {
        return true;
      }
      
      // Already handled F12 in the dedicated handler
      if (e.key === 'F12' || e.keyCode === 123) {
        return;
      }
      
      // Handle other keyboard shortcuts if enabled
      if (enabledRef.current.blockKeyboardShortcuts) {
        const blockedKeys = ['PrintScreen', 'ContextMenu'];
        
        const blockedCombos = [
          { key: 'i', ctrl: true, shift: true }, // Chrome dev tools
          { key: 'j', ctrl: true, shift: true }, // Chrome dev tools
          { key: 'c', ctrl: true, shift: true }, // Inspector
          { key: 'u', ctrl: true }, // View source
          { key: 's', ctrl: true }, // Save page
          { key: 'p', ctrl: true }, // Print
          { key: 'tab', alt: true }, // Switch tabs
          { key: 'tab', ctrl: true }, // Switch tabs
        ];
        
        if (
          blockedKeys.includes(e.key) || 
          blockedCombos.some(combo => 
            e.key.toLowerCase() === combo.key.toLowerCase() &&
            (!('ctrl' in combo) || e.ctrlKey === combo.ctrl) &&
            (!('alt' in combo) || e.altKey === combo.alt) &&
            (!('shift' in combo) || e.shiftKey === combo.shift)
          )
        ) {
          e.preventDefault();
          recordViolation('keyboardShortcut');
          return false;
        }
      }
    };
    
    // We use both capturing phase and normal phase to be extra sure we catch F12
    window.addEventListener('keydown', handleF12, true);
    document.addEventListener('keydown', handleF12, true);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleF12, true);
      document.removeEventListener('keydown', handleF12, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [recordViolation]);

  // Handle devtools detection via window resize and other methods
  useEffect(() => {
    if (!disableF12Key) return;
    
    // Method 1: DevTools changes window dimensions when opened
    let windowWidth = window.innerWidth;
    let windowHeight = window.innerHeight;
    
    const checkDevTools = () => {
      // Check if window dimensions have changed significantly
      const widthThreshold = windowWidth * 0.1; // 10% threshold
      const heightThreshold = windowHeight * 0.1;
      
      const widthDiff = Math.abs(window.innerWidth - windowWidth);
      const heightDiff = Math.abs(window.innerHeight - windowHeight);
      
      if ((widthDiff > widthThreshold || heightDiff > heightThreshold) && 
          disableF12Key && 
          !allowedKeys.includes('F12')) {
        recordViolation('f12Key');
      }
      
      windowWidth = window.innerWidth;
      windowHeight = window.innerHeight;
    };
    
    // Check every second
    const intervalId = setInterval(checkDevTools, 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [disableF12Key, allowedKeys, recordViolation]);

  // Handle right clicks
  useEffect(() => {
    if (!disableRightClick) return;
    
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      recordViolation('rightClick');
      return false;
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, [disableRightClick, recordViolation]);

  // Handle tab/window switching
  useEffect(() => {
    if (!preventTabSwitching) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        recordViolation('tabSwitch');
      }
    };
    
    const handleBlur = () => {
      recordViolation('windowBlur');
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [preventTabSwitching, recordViolation]);

  // Handle window resize
  useEffect(() => {
    if (!preventResize) return;
    
    const handleResize = () => {
      if (window.outerHeight < window.screen.height - 100) {
        recordViolation('windowResize');
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [preventResize, recordViolation]);

  // Enter fullscreen when starting test
  useEffect(() => {
    if (enableFullscreen && !isFullscreen && !securityWarningShown) {
      setSecurityWarningShown(true);
      toast.info(
        "To ensure test integrity, the test will run in fullscreen mode. Attempting to exit may be recorded as a security violation.",
        { 
          duration: 6000,
          action: {
            label: "Start Test",
            onClick: () => enterFullscreen()
          }
        }
      );
    }
    
    return () => {
      if (isFullscreen) {
        exitFullscreen();
      }
    };
  }, [enableFullscreen, enterFullscreen, exitFullscreen, isFullscreen, securityWarningShown]);

  // Reset violations function
  const resetViolations = useCallback(() => {
    setViolations({
      total: 0,
      tabSwitch: 0,
      keyboardShortcut: 0,
      f12Key: 0,
      rightClick: 0,
      windowResize: 0,
      windowBlur: 0
    });
    setIsLocked(false);
  }, []);

  return { 
    violations, 
    isLocked, 
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    resetViolations,
    unlockTest: () => setIsLocked(false)
  };
}
